import { useEffect, useState, useCallback } from "react";
import { ChevronDown, Search, Phone, Mail, Car } from "lucide-react";
import { api } from "../lib/api";
import AdminHeader, { useAdminGuard, inputCls } from "../components/admin/AdminHeader";

const BLOCK_NICE = { "09:00": "9am – 12pm", "10:00": "9am – 12pm", "11:00": "9am – 12pm", "12:00": "12pm – 3pm", "13:00": "12pm – 3pm", "14:00": "12pm – 3pm", "15:00": "3pm – 6pm", "16:00": "3pm – 6pm", "17:00": "3pm – 6pm" };

export default function AdminCustomers() {
  const onError = useAdminGuard();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/customers");
      setCustomers(r.data);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const term = q.trim().toLowerCase();
  const shown = customers.filter((c) =>
    !term || [c.name, c.email, c.phone, ...c.vehicles, ...c.regs].some((v) => (v || "").toLowerCase().includes(term))
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-customers">
      <AdminHeader onRefresh={load} loading={loading} />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tight">Customers</h2>
            <p className="text-sm text-zinc-500" data-testid="customers-count">{customers.length} customers · tap one to see every booking</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, car, reg…" className={`${inputCls} pl-9`} data-testid="customers-search" />
          </div>
        </div>
        <ul className="rounded-sm border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="customers-list">
          {!loading && shown.length === 0 && <li className="p-10 text-center text-zinc-500 text-sm">No customers found.</li>}
          {shown.map((c) => (
            <CustomerRow key={c.key} c={c} open={open === c.key} onToggle={() => setOpen(open === c.key ? null : c.key)} />
          ))}
        </ul>
      </main>
    </div>
  );
}

function CustomerRow({ c, open, onToggle }) {
  const tid = c.key.replace(/[^a-z0-9]/gi, "-");
  return (
    <li data-testid={`customer-${tid}`}>
      <button onClick={onToggle} className="w-full text-left p-5 flex flex-wrap items-center gap-x-8 gap-y-2 hover:bg-white/[0.02] transition-colors" data-testid={`customer-toggle-${tid}`}>
        <div className="min-w-[180px]">
          <div className="font-medium text-white">{c.name}</div>
          <div className="text-xs text-zinc-500">{c.total_bookings} booking{c.total_bookings === 1 ? "" : "s"} · last {c.last_booking_date}</div>
        </div>
        <div className="text-xs text-zinc-400 space-y-0.5 flex-1">
          {c.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.phone}</div>}
          {c.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{c.email}</div>}
        </div>
        <div className="text-xs text-zinc-400 flex-1">
          {c.vehicles.map((v) => <div key={v} className="flex items-center gap-1.5"><Car className="w-3 h-3" />{v}</div>)}
          {c.regs.length > 0 && <div className="mt-1 font-mono text-[#FFD500]">{c.regs.join(" · ")}</div>}
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 grid gap-3" data-testid={`customer-bookings-${tid}`}>
          {c.bookings.map((b) => <BookingDetail key={b.id} b={b} />)}
        </div>
      )}
    </li>
  );
}

function BookingDetail({ b }) {
  const rows = [
    ["Date", `${b.booking_date} · ${BLOCK_NICE[b.time_slot] || b.time_slot}`],
    ["Service", b.service],
    ["Package", b.package],
    ["Starlight", b.starlight_package],
    ["Ambient", b.ambient_package],
    ["Price", b.package_price != null ? `£${Number(b.package_price).toFixed(2)}` : null],
    ["Add-ons", [b.screen_code && "Screen Coded", b.shooting_stars && "Shooting Stars"].filter(Boolean).join(", ")],
    ["Promo", b.promo_code ? `${b.promo_code} (${b.promo_discount_pct}% off)` : null],
    ["Car", b.vehicle],
    ["Reg", b.reg],
    ["Deposit", b.payment_status === "paid" ? `Paid${b.stripe_amount_paid ? ` £${Number(b.stripe_amount_paid).toFixed(2)}` : ""}` : b.payment_status],
    ["Booked", b.created_at ? b.created_at.slice(0, 16).replace("T", " ") : null],
    ["Added by", b.source === "admin" ? "You (manual)" : "Customer (website)"],
    ["Notes", b.notes],
  ].filter(([, v]) => v);
  return (
    <div className="rounded-sm border border-white/5 bg-[#0d0d0d] p-4" data-testid={`customer-booking-${b.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold">{b.package || b.service}</div>
        <span className="text-[10px] tracking-wider px-2 py-1 rounded-full border border-white/15 text-zinc-300 uppercase">{b.status}</span>
      </div>
      <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-2"><dt className="text-zinc-500 w-20 shrink-0">{k}</dt><dd className="text-zinc-200 break-words">{v}</dd></div>
        ))}
      </dl>
    </div>
  );
}
